import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Play, Music2, Sparkles, Eye, Youtube, ArrowRight, Zap } from "lucide-react";
import { listVideos, getChannel, getCategories, formatViews, timeAgo } from "@/lib/api";
import VideoCard from "@/components/VideoCard";

const FILTERS = [
  { id: "all", label: "Tudo", kind: undefined },
  { id: "long", label: "Vídeos", kind: "long" },
  { id: "shorts", label: "Shorts", kind: "shorts" },
  { id: "popular", label: "Populares", sort: "popular" },
];

const Home = () => {
  const [videos, setVideos] = useState([]);
  const [channel, setChannel] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getChannel().then(setChannel).catch(() => {});
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const f = FILTERS.find((x) => x.id === filter);
    const params = {};
    if (f?.kind) params.kind = f.kind;
    if (f?.sort) params.sort = f.sort;
    if (category && category !== "all") params.category = category;
    listVideos(params)
      .then((data) => setVideos(data))
      .catch((err) => { console.error("Failed to load videos:", err); setVideos([]); })
      .finally(() => setLoading(false));
  }, [filter, category]);

  const featured = useMemo(() => {
    // Highest-viewed long-form video as featured (or top overall)
    const longs = videos.filter((v) => !v.is_short);
    const pool = longs.length ? longs : videos;
    return [...pool].sort((a, b) => b.views - a.views)[0];
  }, [videos]);

  const grid = useMemo(() => {
    if (!featured) return videos;
    return videos.filter((v) => v.id !== featured.id);
  }, [videos, featured]);

  return (
    <div data-testid="home-page">
      <Hero channel={channel} featured={featured} />

      {/* Filters */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 mt-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              A biblioteca de <span className="text-[var(--accent)]">Abel</span>
            </h2>
            <p className="text-[var(--text-dim)] text-sm mt-1">
              {channel?.stats?.video_count ?? videos.length} criações · {channel?.stats?.shorts_count ?? "—"} shorts
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="filter-bar">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`pill ${filter === f.id ? "active" : ""}`}
                data-testid={`filter-${f.id}`}
              >
                {f.id === "popular" && <Sparkles className="w-3.5 h-3.5" />}
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="mt-5 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="category-bar">
            <button
              onClick={() => setCategory("all")}
              className={`pill shrink-0 ${category === "all" ? "active" : ""}`}
              data-testid="category-all"
            >
              Todas as categorias
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`pill shrink-0 ${category === c.id ? "active" : ""}`}
                data-testid={`category-${c.id}`}
              >
                {c.label} <span className="opacity-60 text-[11px]">({c.count})</span>
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-10" data-testid="video-grid">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={`skeleton-${i}`} />)
          ) : grid.length === 0 ? (
            <div className="col-span-full text-center text-[var(--text-dim)] py-16">
              Nenhum vídeo encontrado.
            </div>
          ) : (
            grid.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)
          )}
        </div>
      </section>

      <CTA channel={channel} />
    </div>
  );
};

const Hero = ({ channel, featured }) => {
  return (
    <section className="hero-gradient" data-testid="hero-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-12 pb-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)]/60 backdrop-blur text-xs uppercase tracking-[0.18em] text-[var(--text-dim)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot"></span>
            Canal oficial · 🇵🇹
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[0.95] tracking-tight mt-5">
            Música que <br />
            <span className="text-[var(--accent)] italic">toca</span> a alma <br />
            e <span className="italic">inspira</span> o coração.
          </h1>
          <p className="text-[var(--text-dim)] text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
            Bem-vindo às criações de <span className="text-white">Abel Andrade</span> — hinos reinterpretados,
            tributos a <span className="text-white">Cristiano Ronaldo</span> e à Seleção, fado, oração cantada
            e composições originais feitas com verdade.
          </p>
          <div className="flex items-center gap-3 mt-7 flex-wrap">
            {featured && (
              <Link to={`/watch/${featured.id}`} className="pill pill-primary text-base px-5 py-3" data-testid="hero-play-featured">
                <Play className="w-5 h-5 fill-white" />
                Tocar destaque
              </Link>
            )}
            <a
              href={channel?.youtube_url || "https://www.youtube.com/@abelandrade-youtube"}
              target="_blank"
              rel="noreferrer"
              className="pill text-base px-5 py-3"
              data-testid="hero-youtube-link"
            >
              <Youtube className="w-5 h-5" />
              Abrir no YouTube
            </a>
            <Link to="/shorts" className="pill text-base px-5 py-3" data-testid="hero-shorts-link">
              <Zap className="w-5 h-5 text-[var(--accent)]" />
              Feed Shorts
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-8 flex gap-8 text-sm">
            <Stat label="Vídeos" value={channel?.stats?.video_count ?? "—"} />
            <Stat label="Shorts" value={channel?.stats?.shorts_count ?? "—"} />
            <Stat label="Visualizações" value={channel ? formatViews(channel.stats.total_views) : "—"} />
          </div>
        </div>

        {/* Featured card */}
        {featured && (
          <Link
            to={`/watch/${featured.id}`}
            className="block group relative rounded-2xl overflow-hidden border border-[var(--border)] shadow-2xl fade-up"
            style={{ animationDelay: "150ms" }}
            data-testid="hero-featured-card"
          >
            <div className="aspect-video-yt relative">
              <img
                src={featured.thumbnail_hq}
                alt={featured.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={(e) => { e.currentTarget.src = featured.thumbnail; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
              <div className="absolute top-4 left-4 tag bg-[var(--accent)] text-white">
                <Sparkles className="w-3 h-3" /> Em destaque
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-20 h-20 rounded-full bg-[var(--accent)] flex items-center justify-center shadow-2xl">
                  <Play className="w-8 h-8 text-white fill-white ml-1" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-white leading-tight clamp-2">
                  {featured.title}
                </h3>
                <div className="flex items-center gap-3 mt-2 text-xs text-white/80">
                  <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {formatViews(featured.views)}</span>
                  <span>·</span>
                  <span>{timeAgo(featured.published)}</span>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="font-display text-2xl font-semibold">{value}</div>
    <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] mt-0.5">{label}</div>
  </div>
);

const CardSkeleton = () => (
  <div className="animate-pulse">
    <div className="aspect-video-yt rounded-xl bg-[var(--bg-card)]" />
    <div className="h-4 bg-[var(--bg-card)] rounded mt-3 w-3/4" />
    <div className="h-3 bg-[var(--bg-card)] rounded mt-2 w-1/2" />
  </div>
);

const CTA = ({ channel }) => (
  <section className="max-w-[1400px] mx-auto px-4 sm:px-8 mt-24 mb-4">
    <div className="relative overflow-hidden rounded-3xl border border-[var(--border)] p-8 sm:p-12">
      <div className="absolute inset-0 hero-gradient opacity-90" />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--accent)]">
            <Music2 className="w-4 h-4" /> Junta-te à jornada
          </div>
          <h3 className="font-display text-3xl sm:text-4xl font-semibold mt-3 max-w-xl leading-tight">
            Subscreve e sente cada criação <span className="italic text-[var(--accent)]">em primeira mão</span>.
          </h3>
        </div>
        <a
          href={channel?.youtube_url || "https://www.youtube.com/@abelandrade-youtube"}
          target="_blank"
          rel="noreferrer"
          className="pill pill-primary text-base px-6 py-3 shrink-0"
          data-testid="cta-subscribe-button"
        >
          Subscrever no YouTube
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

export default Home;
